import React, { useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { sparkToast, AutoComplete, TooltipTrigger, SparkIcon, Spinner } from '@spark';
import '@/features/datasets/components/sparkSelectFullWidth.css';

const RequiredFieldLabel = ({ children, helpText, style }) => (
    <span style={{
        fontWeight: 300,
        color: '#222',
        marginBottom: 4,
        display: 'block',
        fontSize: 14,
        ...style,
    }}>
        {children} <span style={{ color: '#1990ff', fontSize: 16, marginLeft: 3 }}>*</span>
        {helpText && (
            <TooltipTrigger text={helpText}>
                <span style={{ color: '#878787', marginLeft: 3, display: 'inline-flex', verticalAlign: 'middle', cursor: 'help' }}>
                    <SparkIcon slug="circle-help" size="small" />
                </span>
            </TooltipTrigger>
        )}
    </span>
);
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import ExternalModelResultsSelect from '@/features/datasets/components/DataCollection/ExternalModelResultsSelect';
import DatasetUploadStepsModal from '@/features/datasets/components/DatasetUploadStepsModal';
import { useBenchmarkValidation } from '@/features/datasets/hooks/useBenchmarkValidation';
import { useUsecaseFileRemoval } from '@/features/datasets/hooks/useUsecaseFileRemoval';
import {
    applyChildProgressToCoalescedParentMap,
    initCloneProgressItem,
    librarySourceBaseForCloneGroup,
    markTwoPhaseFailed,
    predictedCloneNameForUsecase,
} from '@/features/datasets/utils/uploadProgress';
import { isSdkTraceDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import { aiLabelRowsToMapping } from '@/features/tasks/utils/aiLabels';
import {
    shouldSubmitAiLabelMappings,
} from '@/features/datasets/utils/hasLabelsHandling';

const FromLibraryTab = ({
    datasetOptions,
    benchmarkOptions,
    currentCheckedDatasets,
    setCurrentCheckedDatasets,
    currentCheckedBenchmarks,
    setCurrentCheckedBenchmarks,
    currentUsecase,
    isExternalFileLoading,
    refreshLibraryOptions,
    subGroup = null,
    /** `'dataset'` or `'benchmark'` shows only that select; omit for both. */
    visibleTarget,
    onDataDetailsVisibilityChange,
    onComplete,
}) => {
    const showDatasetSelect = visibleTarget !== 'benchmark';
    const showBenchmarkSelect = visibleTarget !== 'dataset';
    const {
        refreshUsecases,
        setProgressStatus,
        registerUploadGroup,
        clearCancelledUploadSuppression,
        updateUsecase,
    } = useContext(GlobalContext);
    const { role } = useAuth();
    const { isValidating, validationResult, validateBenchmarkMatch, clearValidation } = useBenchmarkValidation();
    const { confirmRemoval, removeFromUsecase } = useUsecaseFileRemoval({ refreshLibraryOptions });

    const [isBenchmarkLoading, setIsBenchmarkLoading] = useState(false);
    const [isDatasetLoading, setIsDatasetLoading] = useState(false);
    const prevDatasetsRef = useRef(currentCheckedDatasets);
    const prevBenchmarksRef = useRef(currentCheckedBenchmarks);

    const hasExternalPredictions = currentUsecase?.manual_model_predictions?.length > 0;
    const externalPredictionsName = hasExternalPredictions
        ? currentUsecase.manual_model_predictions[0]
        : null;

    useEffect(() => {
        if (isExternalFileLoading) return;
        if (hasExternalPredictions && currentCheckedBenchmarks.length > 0) {
            validateBenchmarkMatch(
                currentUsecase.client_id,
                currentUsecase._id,
                currentCheckedBenchmarks,
                externalPredictionsName
            );
        } else {
            clearValidation();
        }
    }, [
        hasExternalPredictions,
        externalPredictionsName,
        currentCheckedBenchmarks,
        currentUsecase?.client_id,
        currentUsecase?._id,
        isExternalFileLoading,
        validateBenchmarkMatch,
        clearValidation
    ]);

    const cloneDatasetToTask = useCallback((datasetName, isBenchmark, columnMappings) => {
        if (!currentUsecase) return;

        const buildPayload = (targetUsecase) => {
            const payload = {
                client_id: currentUsecase.client_id,
                source_dataset_name: datasetName,
                target_usecase_id: targetUsecase._id,
                is_benchmark: isBenchmark,
                task_type: targetUsecase.task_type,
            };
            if (columnMappings) {
                if (columnMappings.example_column_name) payload.example_column_name = columnMappings.example_column_name;
                if (columnMappings.label_column_name) payload.label_column_name = columnMappings.label_column_name;
                if (columnMappings.relations_column_name) payload.relations_column_name = columnMappings.relations_column_name;
                if (columnMappings.label_mappings) payload.label_mappings = JSON.stringify(columnMappings.label_mappings);
                if (columnMappings.relations_mappings) payload.relations_mappings = JSON.stringify(columnMappings.relations_mappings);
                if (columnMappings.classification_labels_delimiter) {
                    payload.classification_labels_delimiter = columnMappings.classification_labels_delimiter;
                }
                if (columnMappings.empty_multilabel_handling) {
                    payload.empty_multilabel_handling = columnMappings.empty_multilabel_handling;
                }
                if (columnMappings.import_without_labels) {
                    payload.import_without_labels = true;
                }
                if (columnMappings.update_rejection_label) {
                    payload.update_rejection_label = true;
                    payload.rejection_label_name = columnMappings.rejection_label_name || null;
                }
            }
            return payload;
        };

        const attachPredictedName = (uc, predicted) => {
            if (isBenchmark) {
                const b = Array.isArray(uc.benchmarks) ? [...uc.benchmarks] : [];
                if (!b.includes(predicted)) {
                    updateUsecase?.(uc._id, { benchmarks: [...b, predicted] });
                }
            } else {
                const d = Array.isArray(uc.datasets) ? [...uc.datasets] : [];
                if (!d.includes(predicted)) {
                    updateUsecase?.(uc._id, { datasets: [...d, predicted] });
                }
            }
        };

        const revertPredictedName = (uc, predicted) => {
            if (!uc?._id || !predicted) return;
            if (isBenchmark) {
                const b = Array.isArray(uc.benchmarks) ? uc.benchmarks : [];
                updateUsecase?.(uc._id, { benchmarks: b.filter((n) => n !== predicted) });
            } else {
                const d = Array.isArray(uc.datasets) ? uc.datasets : [];
                updateUsecase?.(uc._id, { datasets: d.filter((n) => n !== predicted) });
            }
        };

        /** Move progress entry when the server clone name differs from the predicted key. */
        const resolveProgressKey = (predicted, serverCloneName) => {
            const cloneName = serverCloneName || predicted;
            if (cloneName !== predicted) {
                setProgressStatus((prev) => {
                    const existing = prev?.[predicted];
                    if (!existing) return prev;
                    const next = { ...prev };
                    delete next[predicted];
                    next[cloneName] = {
                        ...existing,
                        datasetName: existing.datasetName || datasetName,
                    };
                    return next;
                });
                const field = isBenchmark ? 'benchmarks' : 'datasets';
                const current = Array.isArray(currentUsecase[field]) ? currentUsecase[field] : [];
                updateUsecase?.(currentUsecase._id, {
                    [field]: [...current.filter((n) => n !== predicted && n !== cloneName), cloneName],
                });
            }
            return cloneName;
        };

        const usecases = (subGroup?.usecases || []).filter((u) => u && u._id);
        const isMultiObjective = usecases.length > 1;

        // "Does your data contain labels? = No" + "Generate labels with AI":
        // label_mappings is present with no label column. Summarization with no
        // summary column also opts into AI generation (no catalog).
        const isAiLabeling = Boolean(
            columnMappings
            && !columnMappings.label_column_name
            && (
                (columnMappings.label_mappings
                    && Object.keys(columnMappings.label_mappings).length > 0)
                || currentUsecase?.task_type === TaskType.summarization
            )
        );

        const applyMultiMerge = (nextIn, groupKey, childCount, predictedClone, childProgress, childIsError) =>
            applyChildProgressToCoalescedParentMap(nextIn, {
                parentKey: groupKey,
                childCount,
                childCloneName: predictedClone,
                childProgress,
                childIsError,
                preserve: (existing) => ({
                    datasetName: existing.datasetName || datasetName,
                    clientId: existing.clientId ?? currentUsecase.client_id,
                    usecaseId: existing.usecaseId ?? currentUsecase._id,
                    isBenchmark: existing.isBenchmark ?? isBenchmark,
                    affectedUsecaseIds: existing.affectedUsecaseIds || usecases.map((u) => u._id),
                }),
            });

        if (!isMultiObjective) {
            const groupKey = librarySourceBaseForCloneGroup(datasetName);
            const predicted = predictedCloneNameForUsecase(groupKey, currentUsecase._id);
            const payload = buildPayload(currentUsecase);

            clearCancelledUploadSuppression?.(predicted);
            initCloneProgressItem({
                setProgressStatus,
                key: predicted,
                datasetName,
                usecaseId: currentUsecase._id,
                clientId: currentUsecase.client_id,
                isBenchmark,
                message: `Cloning '${datasetName}'`,
                affectedUsecaseIds: [currentUsecase._id],
                isLabelGeneration: isAiLabeling,
            });
            attachPredictedName(currentUsecase, predicted);

            data_from_server_post(
                'clone-dataset-to-task',
                payload,
                (response) => {
                    if (response?.status === 'cloning') {
                        const cloneName = resolveProgressKey(predicted, response.clone_name);
                        setProgressStatus((prev) => ({
                            ...prev,
                            [cloneName]: {
                                ...(prev?.[cloneName] || {}),
                                progress: 50,
                                phase: 'processing',
                                message: `Cloning '${datasetName}'`,
                                datasetName,
                                clientId: currentUsecase.client_id,
                                usecaseId: currentUsecase._id,
                                isBenchmark,
                                affectedUsecaseIds: [currentUsecase._id],
                                isLabelGeneration: isAiLabeling,
                                isCompleted: false,
                                isError: false,
                            },
                        }));
                        refreshUsecases();
                        onComplete?.();
                    } else if (response?.status === 'attached') {
                        const cloneName = resolveProgressKey(predicted, response.clone_name);
                        setProgressStatus((prev) => ({
                            ...prev,
                            [cloneName]: {
                                ...(prev?.[cloneName] || {}),
                                progress: 100,
                                uploadProgress: 100,
                                phase: 'completed',
                                isCompleted: true,
                                isError: false,
                                datasetName,
                                clientId: currentUsecase.client_id,
                                usecaseId: currentUsecase._id,
                                isBenchmark,
                                affectedUsecaseIds: [currentUsecase._id],
                                message: 'Processing complete',
                                details: null,
                            },
                        }));
                        refreshUsecases();
                        onComplete?.();
                    } else {
                        sparkToast.error(response?.detail || 'Clone failed');
                        revertPredictedName(currentUsecase, predicted);
                        markTwoPhaseFailed({
                            setProgressStatus,
                            key: predicted,
                            errorMessage: response?.detail || 'Clone failed',
                        });
                        refreshUsecases();
                    }
                },
                (error) => {
                    const message = typeof error === 'string' ? error : 'Failed to clone dataset';
                    sparkToast.error(message);
                    revertPredictedName(currentUsecase, predicted);
                    markTwoPhaseFailed({
                        setProgressStatus,
                        key: predicted,
                        errorMessage: message,
                    });
                    refreshUsecases();
                }
            );
            return;
        }

        const groupKey = librarySourceBaseForCloneGroup(datasetName);
        const childCloneNames = usecases.map((uc) => predictedCloneNameForUsecase(groupKey, uc._id));
        const childCount = childCloneNames.length;
        const affectedIds = usecases.map((u) => u._id);

        clearCancelledUploadSuppression?.(groupKey);
        registerUploadGroup?.(groupKey, childCloneNames, {
            datasetDisplayName: datasetName,
            affectedUsecaseIds: affectedIds,
            clientId: currentUsecase.client_id,
            isBenchmark,
            usecaseIdPrimary: currentUsecase._id,
        });
        initCloneProgressItem({
            setProgressStatus,
            key: groupKey,
            datasetName,
            usecaseId: currentUsecase._id,
            clientId: currentUsecase.client_id,
            isBenchmark,
            message: `Cloning '${datasetName}'`,
            affectedUsecaseIds: affectedIds,
        });

        for (const uc of usecases) {
            const predicted = predictedCloneNameForUsecase(groupKey, uc._id);
            attachPredictedName(uc, predicted);
        }

        const requests = usecases.map(
            (uc) =>
                new Promise((resolve) => {
                    data_from_server_post(
                        'clone-dataset-to-task',
                        buildPayload(uc),
                        (response) => resolve({ uc, response, transportOk: true }),
                        () => resolve({ uc, response: null, transportOk: false })
                    );
                })
        );

        Promise.all(requests).then((rows) => {
            let hadError = false;
            setProgressStatus((prev) => {
                let next = { ...prev };
                for (const row of rows) {
                    const predictedClone = predictedCloneNameForUsecase(groupKey, row.uc._id);
                    if (!row.transportOk) {
                        hadError = true;
                        next = applyMultiMerge(next, groupKey, childCount, predictedClone, 0, true);
                        continue;
                    }
                    const { response } = row;
                    if (response?.status === 'cloning') {
                        next = applyMultiMerge(next, groupKey, childCount, predictedClone, 0, false);
                    } else if (response?.status === 'attached') {
                        next = applyMultiMerge(next, groupKey, childCount, predictedClone, 100, false);
                    } else {
                        hadError = true;
                        next = applyMultiMerge(next, groupKey, childCount, predictedClone, 0, true);
                    }
                }
                return next;
            });
            if (hadError) {
                const bad = rows.find(
                    (r) =>
                        !r.transportOk ||
                        (r.response &&
                            r.response.status !== 'cloning' &&
                            r.response.status !== 'attached')
                );
                sparkToast.error(
                    (bad?.response && bad.response.detail) ||
                    (!bad?.transportOk ? 'Failed to clone dataset' : 'Clone failed for one or more tasks')
                );
                for (const row of rows) {
                    const ok = row.transportOk
                        && (row.response?.status === 'cloning' || row.response?.status === 'attached');
                    if (!ok) {
                        const predictedClone = predictedCloneNameForUsecase(groupKey, row.uc._id);
                        revertPredictedName(row.uc, predictedClone);
                    }
                }
                refreshUsecases();
                return;
            }
            refreshUsecases();
            onComplete?.();
        });
    }, [
        currentUsecase,
        setProgressStatus,
        refreshUsecases,
        subGroup,
        registerUploadGroup,
        clearCancelledUploadSuppression,
        updateUsecase,
        onComplete,
    ]);

    const requestRemoval = useCallback((names, isBenchmark, setChecked) => {
        const sdkName = names.find(isSdkTraceDatasetName);
        confirmRemoval({
            names,
            isBenchmark,
            exportContext: sdkName
                ? {
                    usecase: currentUsecase,
                    datasetName: sdkName,
                    isBenchmark,
                }
                : null,
            onConfirm: async () => {
                setChecked((prev) => prev.filter((v) => !names.includes(v)));
                await removeFromUsecase({
                    usecase: currentUsecase,
                    namesToRemove: names,
                    isBenchmark,
                    currentDatasets: currentCheckedDatasets,
                    currentBenchmarks: currentCheckedBenchmarks,
                });
            },
        });
    }, [confirmRemoval, removeFromUsecase, currentUsecase, currentCheckedDatasets, currentCheckedBenchmarks]);

    // --- Column mapping modal state ---
    const [mappingModalVisible, setMappingModalVisible] = useState(false);
    const [pendingCloneInfo, setPendingCloneInfo] = useState(null);

    useEffect(() => {
        onDataDetailsVisibilityChange?.(mappingModalVisible);
        // The AutoComplete panels are portaled to document.body, so they are not
        // hidden along with this (hidden) modal — close them explicitly.
        if (mappingModalVisible) {
            setDatasetOpen(false);
            setBenchmarkOpen(false);
        }
    }, [mappingModalVisible, onDataDetailsVisibilityChange]);

    const openMappingModal = useCallback((name, isBenchmark, options) => {
        const optionMeta = (options || []).find(o => o.value === name);
        const colMappings = optionMeta?.column_mappings;
        const extraKeys = optionMeta?.extra_params_keys || [];
        const fileColumns = optionMeta?.file_column_names || [];

        // Prefer file_column_names (all original columns); fall back to
        // reconstructing from column_mappings + extra_params_keys for
        // pre-migration datasets.
        let allColumns;
        if (fileColumns.length > 0) {
            allColumns = fileColumns;
        } else {
            const colSet = new Set();
            if (colMappings?.example_column_name) colSet.add(colMappings.example_column_name);
            if (colMappings?.label_column_name) colSet.add(colMappings.label_column_name);
            if (colMappings?.relations_column_name) colSet.add(colMappings.relations_column_name);
            extraKeys.forEach(k => colSet.add(k));
            allColumns = [...colSet];
        }

        // Task-type-aware defaults: always pre-fill example column,
        // only pre-fill label/relations columns when the task types match.
        const sourceTaskType = optionMeta?.task_type;
        const sameTaskType = sourceTaskType && sourceTaskType === currentUsecase?.task_type;
        const isClassificationTask = currentUsecase?.task_type === TaskType.classification;
        const hasClassificationLabels = isClassificationTask || sourceTaskType === TaskType.classification;
        const columnNamesSet = new Set(
            allColumns
                .map(col => (typeof col === 'object' && col !== null ? (col.value || col.label) : col))
                .filter(Boolean)
        );
        if (hasClassificationLabels && !columnNamesSet.has('labels') && !columnNamesSet.has('label')) {
            columnNamesSet.add('labels');
            allColumns = [...allColumns, 'labels'];
        }
        const columnNames = [...columnNamesSet];
        const firstAvailableColumn = (candidates) => candidates.find(candidate => columnNames.includes(candidate));
        const effectiveMappings = {
            example_column_name:
                colMappings?.example_column_name || firstAvailableColumn(['example', 'text']),
            label_column_name: isClassificationTask
                ? ((sameTaskType ? colMappings?.label_column_name : undefined) || firstAvailableColumn(['labels', 'label']))
                : (sameTaskType ? colMappings?.label_column_name : undefined),
            relations_column_name: sameTaskType ? colMappings?.relations_column_name : undefined,
            classification_labels_delimiter: sameTaskType ? colMappings?.classification_labels_delimiter : undefined,
        };

        setPendingCloneInfo({ datasetName: name, isBenchmark, columns: allColumns, columnMappings: effectiveMappings });
        setMappingModalVisible(true);
    }, [currentUsecase?.task_type]);

    const handleMappingDone = useCallback((
        labelMappings,
        exampleColumnName,
        labelColumnName,
        relationsColumnName,
        relationsMappings,
        classificationLabelsDelimiter,
        // The single-objective `onDone` path passes prompt-template metadata in
        // positions 6-11 (unused for clones) and the single-label "Other"/
        // rejection label last; capture it so re-pointing it during the
        // label-review step persists onto the task.
        _examplesAreWithPrompt,
        _promptTemplate,
        _dynamicFieldsList,
        _exampleDynamicField,
        _originLabelsColumnName,
        rejectionLabelName = null,
        // Multilabel classification: how to treat rows whose label list is empty
        // ("none_selected" | "unlabeled" | "exclude"). Applied by the backend
        // clone to already-parsed records.
        emptyMultilabelHandling = null
    ) => {
        if (!pendingCloneInfo) return;
        setMappingModalVisible(false);
        // Classification only — "Does your data contain labels? = No" without AI labels:
        // no label column and no label mappings. Clone the source UNLABELED,
        // snapshotting the original labels into an `origin_labels` extra column.
        // NER clones strip annotations without an origin_labels backup.
        // Summarization with an empty summary column is AI generation (backend
        // ignores import_without_labels for that case).
        const importWithoutLabels = Boolean(
            !labelColumnName
            && (!labelMappings || Object.keys(labelMappings).length === 0)
            && currentUsecase?.task_type !== TaskType.summarization
        );
        // Re-point the single-label classification "Other"/rejection label only
        // when the task is configured for it; never touch it for benchmarks,
        // multi-label, non-classification, or multi-objective clones (the latter
        // route through `onDoneObject` and don't carry a rejection value).
        const isMultiObjectiveClone = (subGroup?.usecases || []).filter((u) => u && u._id).length > 1;
        const supportsRejection = Boolean(
            !pendingCloneInfo.isBenchmark
            && !isMultiObjectiveClone
            && currentUsecase?.task_type === TaskType.classification
            && currentUsecase?.is_multilabel !== true
            && currentUsecase?.confidence_rejection_enabled === true
        );
        const mappings = {
            example_column_name: exampleColumnName || undefined,
            label_column_name: labelColumnName || undefined,
            relations_column_name: relationsColumnName || undefined,
            label_mappings: labelMappings && Object.keys(labelMappings).length > 0 ? labelMappings : undefined,
            relations_mappings: relationsMappings && Object.keys(relationsMappings).length > 0 ? relationsMappings : undefined,
            classification_labels_delimiter: classificationLabelsDelimiter || undefined,
            import_without_labels: importWithoutLabels || undefined,
            update_rejection_label: supportsRejection || undefined,
            rejection_label_name: supportsRejection ? (rejectionLabelName || null) : undefined,
            // How empty response cells are handled (labels / entities /
            // summary / output). Sent whenever a response column is selected.
            empty_multilabel_handling:
                !importWithoutLabels && labelColumnName
                    ? (emptyMultilabelHandling || undefined)
                    : undefined,
        };
        // Optimistically reflect the (re-pointed or cleared) "Fallback" label so
        // EditTaskModal shows the new value immediately; the backend persists
        // the authoritative value during the clone.
        if (supportsRejection && currentUsecase?._id && typeof updateUsecase === 'function') {
            const newRejectionLabelName = rejectionLabelName || null;
            if ((currentUsecase?.rejection_label_name || null) !== newRejectionLabelName) {
                updateUsecase(currentUsecase._id, { rejection_label_name: newRejectionLabelName });
            }
        }
        cloneDatasetToTask(pendingCloneInfo.datasetName, pendingCloneInfo.isBenchmark, mappings);
        setPendingCloneInfo(null);
        // Modal closes via onComplete from cloneDatasetToTask after the clone
        // response — not here — so Data Overview does not refresh before attach.
    }, [pendingCloneInfo, cloneDatasetToTask, currentUsecase, updateUsecase, subGroup]);

    const handleMappingModalCancel = useCallback(() => {
        setMappingModalVisible(false);
        // Revert the Select state — remove the item that was optimistically added
        if (pendingCloneInfo) {
            const { datasetName, isBenchmark } = pendingCloneInfo;
            if (isBenchmark) {
                setCurrentCheckedBenchmarks(prev => prev.filter(v => v !== datasetName));
                prevBenchmarksRef.current = prevBenchmarksRef.current.filter(v => v !== datasetName);
            } else {
                setCurrentCheckedDatasets(prev => prev.filter(v => v !== datasetName));
                prevDatasetsRef.current = prevDatasetsRef.current.filter(v => v !== datasetName);
            }
        }
        setPendingCloneInfo(null);
    }, [pendingCloneInfo, setCurrentCheckedDatasets, setCurrentCheckedBenchmarks]);

    const handleDatasetChange = (vals) => {
        const newDatasets = Array.isArray(vals) ? vals : (vals ? [vals] : []);
        const prevDatasets = prevDatasetsRef.current;

        const added = newDatasets.filter(d => !prevDatasets.includes(d));
        const removed = prevDatasets.filter(d => !newDatasets.includes(d));

        if (added.length > 0) {
            setCurrentCheckedDatasets(prev => [...new Set([...prev, ...added])]);
            prevDatasetsRef.current = [...new Set([...prevDatasetsRef.current, ...added])];
            added.forEach(name => openMappingModal(name, false, datasetOptions));
        }

        if (removed.length > 0) {
            requestRemoval(removed, false, setCurrentCheckedDatasets);
        }
    };

    const handleBenchmarkChange = (vals) => {
        const newBenchmarks = Array.isArray(vals) ? vals : (vals ? [vals] : []);
        const prevBenchmarks = prevBenchmarksRef.current;

        const added = newBenchmarks.filter(b => !prevBenchmarks.includes(b));
        const removed = prevBenchmarks.filter(b => !newBenchmarks.includes(b));

        if (added.length > 0) {
            setCurrentCheckedBenchmarks(prev => [...new Set([...prev, ...added])]);
            prevBenchmarksRef.current = [...new Set([...prevBenchmarksRef.current, ...added])];
            added.forEach(name => openMappingModal(name, true, benchmarkOptions));
        }

        if (removed.length > 0) {
            requestRemoval(removed, true, setCurrentCheckedBenchmarks);
        }
    };

    useEffect(() => {
        prevDatasetsRef.current = currentCheckedDatasets;
    }, [currentCheckedDatasets]);

    useEffect(() => {
        prevBenchmarksRef.current = currentCheckedBenchmarks;
    }, [currentCheckedBenchmarks]);

    // --- Spark AutoComplete (searchable multi-select) local UI state ---
    const [datasetOpen, setDatasetOpen] = useState(false);
    const [datasetQuery, setDatasetQuery] = useState('');
    const [benchmarkOpen, setBenchmarkOpen] = useState(false);
    const [benchmarkQuery, setBenchmarkQuery] = useState('');

    const optionLabel = (options, value) => {
        const opt = (options || []).find((o) => o.value === value);
        return opt?.label != null ? String(opt.label) : value;
    };

    const buildItems = (options, selectedValues, query, onToggle) => {
        const normalized = (query || '').trim().toLowerCase();
        return (options || [])
            .filter((o) => String(o?.label ?? '').toLowerCase().includes(normalized))
            .map((o) => ({
                label: String(o.label ?? o.value),
                checked: selectedValues.includes(o.value),
                onClick: () => onToggle(o.value),
            }));
    };

    const toggleDataset = (value) => {
        const next = currentCheckedDatasets.includes(value)
            ? currentCheckedDatasets.filter((v) => v !== value)
            : [...currentCheckedDatasets, value];
        handleDatasetChange(next);
    };

    const toggleBenchmark = (value) => {
        const next = currentCheckedBenchmarks.includes(value)
            ? currentCheckedBenchmarks.filter((v) => v !== value)
            : [...currentCheckedBenchmarks, value];
        handleBenchmarkChange(next);
    };

    const datasetItems = useMemo(
        () => buildItems(datasetOptions, currentCheckedDatasets, datasetQuery, toggleDataset),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [datasetOptions, currentCheckedDatasets, datasetQuery]
    );
    const benchmarkItems = useMemo(
        () => buildItems(benchmarkOptions, currentCheckedBenchmarks, benchmarkQuery, toggleBenchmark),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [benchmarkOptions, currentCheckedBenchmarks, benchmarkQuery]
    );

    const datasetTags = currentCheckedDatasets.map((v) => optionLabel(datasetOptions, v));
    const benchmarkTags = currentCheckedBenchmarks.map((v) => optionLabel(benchmarkOptions, v));

    return (
        <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px'
        }}>
            {showDatasetSelect && (
            <div>
                <RequiredFieldLabel
                    helpText="Dataset is a collection of data that is used to train a model."
                >
                    <span className="fs-unmask">Select datasets</span>
                </RequiredFieldLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                        style={{ flex: 1, minWidth: 0 }}
                        onClick={() => { if (role !== 'Viewer') setDatasetOpen(true); }}
                        onFocus={() => { if (role !== 'Viewer') setDatasetOpen(true); }}
                    >
                        <AutoComplete
                            type="multi-select"
                            size="medium"
                            placeholder="Select"
                            open={datasetOpen}
                            onOpenChange={(next) => {
                                setDatasetOpen(next);
                                if (!next) setDatasetQuery('');
                            }}
                            value={datasetQuery}
                            tags={datasetTags}
                            items={datasetItems}
                            disabled={role === 'Viewer'}
                            onChange={(e) => setDatasetQuery(e.target.value)}
                            onTagRemove={(_tag, index) => toggleDataset(currentCheckedDatasets[index])}
                            className="spark-select-full-width"
                        />
                    </div>
                    {isDatasetLoading ? <Spinner size="small" /> : null}
                </div>
            </div>
            )}

            {showBenchmarkSelect && (
            <div>
                <RequiredFieldLabel
                    helpText="Benchmark is a collection of data that is used to evaluate the performance of a model."
                >
                    <span className="fs-unmask">Select benchmarks</span>
                </RequiredFieldLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                        style={{ flex: 1, minWidth: 0 }}
                        onClick={() => { if (role !== 'Viewer') setBenchmarkOpen(true); }}
                        onFocus={() => { if (role !== 'Viewer') setBenchmarkOpen(true); }}
                    >
                        <AutoComplete
                            type="multi-select"
                            size="medium"
                            placeholder="Select"
                            open={benchmarkOpen}
                            onOpenChange={(next) => {
                                setBenchmarkOpen(next);
                                if (!next) setBenchmarkQuery('');
                            }}
                            value={benchmarkQuery}
                            tags={benchmarkTags}
                            items={benchmarkItems}
                            disabled={role === 'Viewer'}
                            onChange={(e) => setBenchmarkQuery(e.target.value)}
                            onTagRemove={(_tag, index) => toggleBenchmark(currentCheckedBenchmarks[index])}
                            className="spark-select-full-width"
                        />
                    </div>
                    {(isBenchmarkLoading || isValidating || isExternalFileLoading) ? (
                        <TooltipTrigger text="Validating benchmark matches with external model results">
                            <span style={{ display: 'inline-flex' }}>
                                <Spinner size="small" />
                            </span>
                        </TooltipTrigger>
                    ) : null}
                </div>
            </div>
            )}

            {/* EMR only in the unscoped (both-selects) library modal — card-scoped
                openings show a single select and must not surface this field. */}
            {!visibleTarget && currentUsecase?.benchmarks?.length > 0 && (
                <ExternalModelResultsSelect
                    currentUsecase={currentUsecase}
                    onUpdateSuccess={refreshUsecases}
                    isValidating={isValidating || isExternalFileLoading}
                    validationResult={validationResult}
                />
            )}

            <DatasetUploadStepsModal
                visible={mappingModalVisible}
                onCancel={handleMappingModalCancel}
                fileName={pendingCloneInfo?.datasetName}
                uploadedFileName={pendingCloneInfo?.datasetName}
                preloadedColumns={pendingCloneInfo?.columns}
                defaultColumnMappings={pendingCloneInfo?.columnMappings}
                isBenchmark={!!pendingCloneInfo?.isBenchmark}
                taskType={currentUsecase?.task_type}
                isMultilabel={currentUsecase?.is_multilabel}
                existingRejectionLabelName={currentUsecase?.rejection_label_name || null}
                confidenceRejectionEnabled={currentUsecase?.confidence_rejection_enabled === true}
                clientId={currentUsecase?.client_id}
                llmUsecaseId={currentUsecase?._id}
                onDone={handleMappingDone}
                onNext={handleMappingDone}
                subGroup={subGroup}
                onDoneObject={(payload) => {
                    const out = payload?.outputs?.[currentUsecase?._id] || {};
                    // "Does your data contain labels? = No": AI catalog lives on
                    // `out.aiLabels` when generate-with-AI; unlabeled clears mappings.
                    const noLabels = out.hasLabels === false;
                    const submitAi = shouldSubmitAiLabelMappings(out);
                    const labelMappings = submitAi
                        ? aiLabelRowsToMapping(out.aiLabels || [])
                        : noLabels
                          ? {}
                          : out.labelMappings || {};
                    handleMappingDone(
                        labelMappings,
                        payload?.examples?.exampleColumnName,
                        noLabels ? '' : (out.labelColumn || ''),
                        noLabels ? null : out.relationsColumn || null,
                        out.relationsMappings || {},
                        out.delimiter,
                        undefined, // _examplesAreWithPrompt (unused for clones)
                        undefined, // _promptTemplate
                        undefined, // _dynamicFieldsList
                        undefined, // _exampleDynamicField
                        undefined, // _originLabelsColumnName
                        null, // rejectionLabelName (multi-objective clones don't re-point it)
                        out.emptyMultilabelHandling
                    );
                }}
            />
        </div>
    );
};

export default FromLibraryTab;
